import { useCallback, useEffect, useState } from "react";
import { poolApi } from "../../services/businessApi";
import { Button } from "../common/Button";
import { PutAwayDisclosure, splitPutAway } from "../common/PutAwaySection";
import { Badge } from "../common/Badge";
import { DataTable } from "../common/DataTable";
import { Modal } from "../common/Modal";
import { Field } from "../common/Field";
import { PromptDialog } from "../common/PromptDialog";
import { useToast } from "../../context/ToastContext";
import { vocabularyFor, toPounds, weightLabel } from "../../services/productVocabulary";
import { money } from "../../utils/format";

const MUTED = { color: "var(--color-text-muted)" };

/**
 * The jar-size list, with its editing controls and NO CARD OF ITS OWN.
 *
 * ── WHY THIS IS A SEPARATE COMPONENT (#410) ──
 *
 * The same split VarietalManager already is, for the same reason, and
 * this file exists because the two halves of #410 had drifted apart.
 *
 * "By varietal" opens its manager IN PLACE: the card keeps its heading,
 * the table is replaced by the editable list, Add sits in the header and
 * Done at the bottom. One heading, one Add, one Done.
 *
 * The shelf card opened jar sizes differently. Its own header toggle
 * flipped to "Done" and then a whole second Card titled "Bottle sizes"
 * rendered underneath, carrying its own Add and its own Done -- so
 * managing put TWO Dones and TWO Adds on screen, over two lists of the
 * same jar sizes. Tests now assert all three counts.
 *
 * That is the duplication #439 had just finished removing from this very
 * page (two bottled tables merged into one shelf), reintroduced by the
 * control #439 added. A card cannot be nested in a card, so the fix is
 * to have a manager that is not a card -- this.
 *
 * VarietalManager's note applies verbatim: a Done in the header would be
 * a second control for the way out, and the way out belongs at the
 * bottom of the list, where you are once you have finished reading it.
 *
 * ── WHY `adding` ARRIVES FROM OUTSIDE ──
 *
 * Add belongs in the CARD HEADER, beside the heading, which is the
 * placement the reporter preferred and which only the card can render.
 * So the button lives with the caller and the dialog lives here.
 *
 * ── THE RETIRED ONES, ONLY WHILE MANAGING ──
 *
 * The list endpoint filters to is_active = 1, so a size put away vanished
 * from the only list that could bring it back. This asks for the wider
 * list -- but only while managing, because a picker that inherited it
 * would start offering sizes somebody had deliberately put away.
 *
 * ── THE LABEL MOVES, THE WEIGHT NEVER DOES ──
 *
 * There is deliberately no way to edit `pounds`, and the API has no field
 * for it. Bottling history joins on bottle_size_id and reads the label
 * live, so a rename propagates through every past run at once. The weight
 * is the opposite: it was subtracted from the pool per jar AT THE TIME,
 * so changing it would not recompute anything -- it would quietly change
 * what last season's rows appear to mean. A size whose weight is wrong is
 * a NEW size, and the wrong one gets put away.
 */
export function BottleSizeManager({ sizes, adding, onAddingClose, onChanged, onClose,
                                   productType = "Honey" }) {
  const { notify } = useToast();
  const [renaming, setRenaming] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [all, setAll] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const r = await poolApi.listSizes(true, productType);
      setAll(r.bottle_sizes || []);
    } catch {
      // Best-effort: `sizes` from the caller is already on screen, and a
      // failed load here must not take the card down.
      setAll(null);
    }
  }, [productType]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function change(size, changes, said) {
    setBusy(true);
    try {
      await poolApi.updateSize(size.bottle_size_id, changes);
      notify(said, "success");
      await loadAll();
      // The bottling and emptying pickers on the page are holding the old
      // list. Without this a size just put away stays selectable.
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not change that jar size", "danger");
    } finally {
      setBusy(false);
      setRenaming(null);
      setPricing(null);
    }
  }

  const rows = all ?? sizes;

  const { active: activeRows, putAway: putAwayRows } =
    splitPutAway(rows ?? [], (r) => r.is_active !== false);
  const [showPutAway, setShowPutAway] = useState(false);

  return (
    <>
      <p style={{ marginTop: 0, ...MUTED }}>
        Renaming one updates it everywhere, including jars you have already
        filled. Putting one away stops it being offered for new bottling and
        changes nothing you have already recorded. The weight cannot be
        changed — it is what past runs took out of the pool.
      </p>

      {/*
        * PUT AWAY GOES TO THE BOTTOM, BEHIND A DISCLOSURE (#486).
        *
        * `r.is_active === false` is how this file reads the flag, and the
        * distinction matters: the list endpoint historically omitted the
        * column, so an ABSENT flag here means active. Testing truthiness
        * instead would file every row from that endpoint as put away.
        */}
      <DataTable
        rows={showPutAway ? [...activeRows, ...putAwayRows] : activeRows}
        rowKey="bottle_size_id"
        columns={[
          {
            key: "label",
            header: "Name",
            render: (r) => (
              <>
                {r.label}
                {r.is_active === false && <> <Badge tone="neutral">Put away</Badge></>}
              </>
            ),
          },
          /* READ BACK in the unit it was entered in. A 4 oz pollen jar
             stored as 0.25 lb and shown as "0.25 lb" is the app handing
             back a different number than the one typed in. */
          { key: "pounds", header: "Weight",
            render: (r) => weightLabel(productType, r.pounds) },
          /* Other products only (#520): what Send to vendor pre-fills. */
          ...(productType === "Honey" ? [] : [{
            key: "default_price",
            header: "Default price",
            render: (r) => (r.default_price == null ? "—" : money(r.default_price)),
          }]),
          {
            key: "actions",
            header: "",
            render: (r) => (
              <>
                <Button size="sm" variant="secondary" disabled={busy}
                  onClick={() => setRenaming(r)}>
                  Rename
                </Button>
                {" "}
                {productType === "Honey" ? null : (
                  <>
                    <Button size="sm" variant="secondary" disabled={busy}
                      onClick={() => setPricing(r)}>
                      Set price
                    </Button>
                    {" "}
                    {r.default_price == null ? null : (
                      <>
                        <Button size="sm" variant="secondary" disabled={busy}
                          onClick={() => change(r, { default_price: null }, "Default price cleared.")}>
                          Clear price
                        </Button>
                        {" "}
                      </>
                    )}
                  </>
                )}
                {r.is_active === false ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => change(r, { is_active: true }, `"${r.label}" is back on your list.`)}
                  >
                    Bring back
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => change(
                      r,
                      { is_active: false },
                      // "Put away", not "deleted": bottling history still
                      // names this size, and saying deleted would imply
                      // that history went too.
                      `"${r.label}" put away. Jars already filled in this size are unchanged.`,
                    )}
                  >
                    Put away
                  </Button>
                )}
              </>
            ),
          },
        ]}
        emptyTitle="No bottle sizes yet"
        emptyDescription="Add the jars you actually fill — 1 lb, 12 oz, whatever you use."
      />
      <PutAwayDisclosure
        count={putAwayRows.length}
        open={showPutAway}
        onToggle={() => setShowPutAway((v) => !v)}
      />

      {/* AT THE BOTTOM, where you are once you have read the list. A Done
          in the header would be a second control for the way out -- which
          is exactly the bug this component was extracted to fix. */}
      <Button variant="secondary" onClick={onClose} style={{ marginTop: "var(--space-3)" }}>
        Done
      </Button>

      <PromptDialog
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        onSubmit={(label) => (label
          ? change(renaming, { label }, `Renamed to ${label}.`)
          : setRenaming(null))}
        title="Rename this jar size"
        label={renaming ? `What should "${renaming.label}" be called?` : ""}
        // Opens holding the current name, so fixing a typo is a keystroke
        // rather than retyping the whole label.
        initialValue={renaming ? renaming.label : ""}
        hint="This updates the name everywhere, including jars you have already filled. The weight stays as it is."
        confirmLabel="Rename"
        busy={busy}
        // 50, matching app.BottleSizes.label and BottleSizeUpdateRequest.
        // A prompt that accepts more than the column does refuses the name
        // only after the user presses Rename.
        maxLength={50}
      />

      <PromptDialog
        open={pricing !== null}
        onClose={() => setPricing(null)}
        onSubmit={(text) => {
          const value = Number(String(text).trim());
          if (Number.isNaN(value) || value < 0) return notify("Enter a price", "warning");
          return change(pricing, { default_price: value }, `Default price is ${money(value)}.`);
        }}
        title="Default price"
        label={pricing ? `What do you usually charge for one "${pricing.label}"?` : ""}
        initialValue={pricing && pricing.default_price != null ? String(pricing.default_price) : ""}
        hint="Fills in the price when you send this size to a vendor. You can still change it each time. Use Clear price to remove it."
        confirmLabel="Save"
        busy={busy}
        maxLength={10}
      />

      <AddSizeDialog
        productType={productType}
        open={Boolean(adding)}
        onClose={() => onAddingClose?.()}
        onAdded={async () => {
          onAddingClose?.();
          await loadAll();
          onChanged?.();
        }}
      />
    </>
  );
}

/**
 * Two fields, both required, so not a PromptDialog.
 *
 * The weight is the one that matters: the pool is kept in pounds and
 * bottling twelve jars subtracts twelve times this number. A size with a
 * guessed weight would do arithmetic wrongly and silently, which is why
 * this refuses rather than defaulting to 1 -- the same refusal mobile's
 * JarSizeManager makes, in the same words.
 */
function AddSizeDialog({ open, onClose, onAdded, productType = "Honey" }) {
  const v = vocabularyFor(productType);
  const { notify } = useToast();
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setLabel(""); setAmount(""); setPrice(""); setBusy(false); }
  }, [open]);

  async function submit() {
    /* ENTERED in the product's own unit, STORED in pounds. A pollen jar
       is 4 oz, not 0.25 lb, and asking for the pound figure makes
       somebody do the division -- which is the arithmetic the app is for.
       V120's DECIMAL(12,4) holds the conversion exactly. */
    const entered = Number(amount);
    if (!label.trim()) return notify(`Give the ${v.unit} a name — whatever you call it`, "warning");
    if (!entered || entered <= 0) {
      return notify(`How much does one ${v.unit} hold, in ${v.entryUnit}?`, "warning");
    }
    setBusy(true);
    try {
      await poolApi.createSize(label.trim(), toPounds(productType, entered), productType,
        ...(price === "" ? [] : [Number(price)]));
      notify(`${v.unit[0].toUpperCase()}${v.unit.slice(1)} size saved`, "success");
      onAdded();
    } catch (err) {
      notify(err.apiMessage || "Could not save the size", "danger");
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Add a ${v.unit} size`}
      footer={<Button onClick={submit} disabled={busy}>Save Size</Button>}>
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        {/* THE HINTS CARRY THE PRODUCT'S OWN WORDS (#430). "How much
            honey one jar holds" is wrong on a wax screen in two places at
            once, and the examples matter as much as the noun -- "1 lb, 12
            oz, honey bear" tells a wax seller nothing. The size LABEL is
            still their free text; this is only the chrome around it. */}
        {/* THE EXAMPLE COMES FROM THE PRODUCT, not from its noun. This
            asked whether the unit was a "jar" and offered honey's
            examples if so -- which was fine while only honey had jars,
            and started offering "honey bear" on a pollen screen the
            moment pollen's pouch became a jar (4 Oct). A product's words
            belong to the product. */}
        <Field label="Name" hint={`What you call it — ${v.example}.`}>
          {(id, aria) => (
            <input id={id} {...aria} className="touch-target" value={label}
              maxLength={50} onChange={(e) => setLabel(e.target.value)} />
          )}
        </Field>
        <Field label={`Weight in ${v.entryUnit === "oz" ? "ounces" : "pounds"}`}
          hint={`How much one ${v.unit} holds. ${v.verb === "Bottle" ? "Bottling" : `${v.verb}ing`} takes this much out of the pool per ${v.unit}.`}>
          {(id, aria) => (
            <input id={id} {...aria} className="touch-target" type="number"
              /* A finer step in ounces: the smallest honey jar is a
                 fraction of a pound, the smallest pollen jar is a
                 fraction of an ounce. */
              step={v.entryUnit === "oz" ? "0.01" : "0.001"}
              min={v.entryUnit === "oz" ? "0.01" : "0.001"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)} />
          )}
        </Field>
        {productType === "Honey" ? null : (
          <Field label="Default price (optional)"
            hint={`What you usually charge for one ${v.unit}. Fills in the price when you send it to a vendor.`}>
            {(id, aria) => (
              <input id={id} {...aria} className="touch-target" type="number" step="0.01" min="0"
                value={price} onChange={(e) => setPrice(e.target.value)} />
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
}
